import { siteNames } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Stack,
  Text,
  Columns,
  Column,
  Toggle,
  TooltipRenderer,
  IconInfo,
  Inline,
  Notice,
  useResponsiveValue,
  Alert,
} from 'braid-design-system';
import { observer } from 'mobx-react';
import type React from 'react';

import type { FilterFns } from 'src/components/app/FilterSideBar/useFilters';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { CONTROL_TYPE } from 'src/constants/roleRequirementsTypes';
import type { IFilterQuestion } from 'src/stores/JobApplicationStore/FiltersStore/FiltersStore';
import type { IQuestionnaireStore } from 'src/stores/JobStore/QuestionnaireStore';
import { STATE_LOADED } from 'src/stores/state';

import { ResponsiveText } from '../ResponsiveText';

import translations from './.vocab/';
import { CheckboxFilterAnswers } from './CheckboxFilterAnswers';
import { QuestionItem } from './QuestionItem';
import { RangeFilter } from './RangeFilter';

interface FilterScreeningQuestionProps {
  questionnaire: IQuestionnaireStore;
  onParentFilterSelected: FilterFns['onParentFilterSelected'];
  onFilterSelected: FilterFns['onFilterSelected'];
  onMustHaveFilterToggled: FilterFns['onMustHaveFilterToggled'];
  isMustHaveToggleSelected: boolean;
  questions: IFilterQuestion[];
}

const FilterScreeningQuestion: React.FC<FilterScreeningQuestionProps> = ({
  questionnaire,
  onParentFilterSelected,
  onFilterSelected,
  onMustHaveFilterToggled,
  isMustHaveToggleSelected,
  questions,
}) => {
  const { t } = useTranslations(translations);
  const { site } = useAppConfig();

  const isMobile =
    useResponsiveValue()({
      mobile: true,
      tablet: false,
    }) ?? false;

  const hasFilterQuestions = questions.length > 0;
  const shouldShowMustHaveToggle =
    questions.filter((question) => question.questionInfo.essential).length > 0;
  const showRRError = questionnaire?.isError;
  const showFiltersSection = hasFilterQuestions || showRRError;

  if (questionnaire.state !== STATE_LOADED && !showRRError) {
    return <></>;
  }

  const heading = isMobile ? (
    <ResponsiveText
      size={{ mobile: 'standard', tablet: 'small' }}
      fallbackSize="small"
    >
      {t('Your preferred answers are marked with *')}
    </ResponsiveText>
  ) : (
    <Columns space="xsmall">
      <Column>
        <Inline alignY="center" space="xsmall">
          <Box
            paddingY="xxsmall"
            data={{
              intercomid: 'screeningQuestions',
            }}
          >
            <Text tone="neutral" size="small" weight="strong">
              {t('Screening questions')}
            </Text>
          </Box>

          <TooltipRenderer
            id="screen-questions-tooltip"
            tooltip={
              <Text>{t('Your preferred answers are marked with *')}</Text>
            }
            placement="top"
          >
            {({ triggerProps }) => (
              <Box
                aria-label={t('Screen Questions Filters Help')}
                {...triggerProps}
                data-testid="screen-help-icon"
              >
                <IconInfo size="xsmall" tone="neutral" />
              </Box>
            )}
          </TooltipRenderer>
        </Inline>
      </Column>
    </Columns>
  );

  if (
    !hasFilterQuestions &&
    !showRRError &&
    (site === siteNames.employer.seek.th || site === siteNames.employer.seek.hk)
  ) {
    return (
      <Layout
        heading={heading}
        content={
          <Box>
            <Notice tone="critical">
              <Text>
                {/* FIXME: This is missing ID translations */}
                {t(
                  'There are no filters for this job ad. To set filters, ask screening questions when you post your next job ad.',
                )}
              </Text>
            </Notice>
          </Box>
        }
      />
    );
  }

  if (!showFiltersSection) {
    return <></>;
  }

  if (showRRError) {
    return (
      <Layout
        heading={heading}
        content={
          <Box
            width="full"
            data-cy="questionnaire-error"
            margin="xsmall"
            paddingBottom="medium"
          >
            <Alert tone="critical">
              <Text>{t("Filters aren't available right now...")}</Text>
            </Alert>
          </Box>
        }
      />
    );
  }

  return (
    <Layout
      heading={heading}
      content={
        <Stack space="medium">
          {shouldShowMustHaveToggle && (
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="flexStart"
              alignItems="center"
              paddingBottom="xsmall"
            >
              <Toggle
                size={isMobile ? 'standard' : 'small'}
                label={t('Apply must-have requirement filters')}
                onChange={onMustHaveFilterToggled}
                id="must-have-toggle"
                on={isMustHaveToggleSelected}
              />
              <TooltipRenderer
                id="filters-toggle-tooltip"
                tooltip={
                  <Text>
                    {t(
                      'Must-have questions were set by you or another account...',
                    )}
                  </Text>
                }
                placement="top"
              >
                {({ triggerProps }) => (
                  <Box
                    aria-label={t('RR Filters Help')}
                    {...triggerProps}
                    paddingLeft="xsmall"
                    data-testid="rr-help-icon"
                  >
                    <IconInfo size="xsmall" />
                  </Box>
                )}
              </TooltipRenderer>
            </Box>
          )}

          <Stack space="xlarge">
            {questions.map((question) => (
              <QuestionItem
                key={question.questionInfo.globalIdOrId}
                id={question.questionInfo.globalIdOrId}
                label={question.questionInfo.statement ?? ''}
                isSelected={question.isSelected}
                onSelect={() => {
                  question.onToggleQuestionSelected();
                  onParentFilterSelected(question.isSelected);
                }}
                expanded={question.isExpanded}
                disabled={question.disabled}
                onToggle={() => {
                  question.onToggleQuestionExpanded();
                }}
              >
                {question.questionInfo.getHirerControlType() ===
                CONTROL_TYPE.RANGE ? (
                  <RangeFilter
                    question={question}
                    onSelect={onFilterSelected}
                  />
                ) : (
                  <CheckboxFilterAnswers
                    question={question}
                    onSelect={onFilterSelected}
                  />
                )}
              </QuestionItem>
            ))}
          </Stack>
        </Stack>
      }
    />
  );
};

const Layout: React.FC<{ heading: JSX.Element; content: JSX.Element }> = ({
  heading,
  content,
}) => (
  <Box id="screening-questions-intercom">
    <Stack space="medium">
      {heading}
      {content}
    </Stack>
  </Box>
);

export default observer(FilterScreeningQuestion);
